import { useState } from "react";
import { Navigate, NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LayoutGrid, Package, ReceiptText, Truck, Settings as Cog, LogOut, ExternalLink, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Logo } from "@/components/Logo";
import { LangSwitcher } from "@/components/Header";
import { errMsg } from "@/lib/api";

export function AdminLogin() {
  const { user, login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password);
      nav("/admin");
    } catch (ex) {
      setErr(errMsg(ex, t("a_invalid")));
    }
    setBusy(false);
  };

  return (
    <main className="grain relative flex min-h-screen items-center justify-center bg-[#0B0B0C] px-5 text-[#F7F3EB]" data-testid="admin-login-page">
      <div className="absolute end-5 top-5"><LangSwitcher /></div>
      <form onSubmit={submit} className="w-full max-w-sm">
        <Logo />
        <h1 className="font-display mt-12 text-4xl">{t("a_login_title")}</h1>
        <label className="mt-10 block">
          <span className="text-xs tracking-[0.2em] text-[#F7F3EB]/70 rtl:tracking-normal rtl:text-sm">{t("a_email")}</span>
          <input data-testid="admin-login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr" className="mt-1 w-full border-0 border-b border-[#F7F3EB]/25 bg-transparent py-3 outline-none focus:border-[#C6A76A]" />
        </label>
        <label className="mt-6 block">
          <span className="text-xs tracking-[0.2em] text-[#F7F3EB]/70 rtl:tracking-normal rtl:text-sm">{t("a_password")}</span>
          <input data-testid="admin-login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" className="mt-1 w-full border-0 border-b border-[#F7F3EB]/25 bg-transparent py-3 outline-none focus:border-[#C6A76A]" />
        </label>
        {err && <p data-testid="admin-login-error" className="mt-4 text-sm text-[#e8a0a0]">{err}</p>}
        <button data-testid="admin-login-submit-btn" disabled={busy} className="mt-10 flex w-full items-center justify-center gap-2 bg-[#C6A76A] py-4 text-xs tracking-[0.25em] text-[#0B0B0C] transition-colors hover:bg-[#E3CA95] disabled:opacity-50 rtl:text-sm rtl:tracking-normal">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("a_sign_in")}
        </button>
      </form>
    </main>
  );
}

const LINKS = [
  ["/admin", "a_overview", LayoutGrid, true],
  ["/admin/products", "a_products", Package],
  ["/admin/orders", "a_orders", ReceiptText],
  ["/admin/delivery", "a_delivery", Truck],
  ["/admin/settings", "a_settings", Cog],
];

export function AdminShell() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-[#0B0B0C]"><Loader2 className="h-6 w-6 animate-spin text-[#C6A76A]" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#F7F3EB] text-[#0B0B0C] lg:flex" data-testid="admin-dashboard">
      <aside className="bg-[#0B0B0C] text-[#F7F3EB] lg:sticky lg:top-0 lg:h-screen lg:w-64 lg:shrink-0">
        <div className="flex items-center justify-between px-5 py-5 lg:block lg:px-7 lg:py-8">
          <Link to="/admin"><Logo /></Link>
          <div className="flex items-center gap-1 lg:mt-6"><LangSwitcher /></div>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:px-4">
          {LINKS.map(([to, key, Icon, end]) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${key}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-3 px-4 py-3 text-sm transition-colors ${isActive ? "bg-[#241914] text-[#C6A76A]" : "text-[#F7F3EB]/75 hover:text-[#F7F3EB]"}`}
            >
              <Icon className="h-4 w-4" strokeWidth={1.3} /> {t(key)}
            </NavLink>
          ))}
        </nav>
        <div className="hidden px-4 lg:absolute lg:bottom-6 lg:block lg:w-64">
          <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-store" className="flex items-center gap-3 px-4 py-3 text-sm text-[#F7F3EB]/75 hover:text-[#F7F3EB]"><ExternalLink className="h-4 w-4" strokeWidth={1.3} /> {t("a_view_store")}</a>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex w-full items-center gap-3 px-4 py-3 text-sm text-[#F7F3EB]/75 hover:text-[#F7F3EB]"><LogOut className="h-4 w-4" strokeWidth={1.3} /> {t("a_logout")}</button>
          <p className="truncate px-4 pt-2 text-xs text-[#F7F3EB]/50" dir="ltr">{user.email}</p>
        </div>
      </aside>
      <div className="flex-1 px-5 py-8 sm:px-10 lg:py-12">
        <div className="mb-6 flex justify-end gap-4 lg:hidden">
          <a href="/" target="_blank" rel="noreferrer" className="text-xs underline">{t("a_view_store")}</a>
          <button data-testid="admin-logout-btn-mobile" onClick={logout} className="text-xs underline">{t("a_logout")}</button>
        </div>
        <Outlet />
      </div>
    </div>
  );
}

export function AdminHeading({ title, children }) {
  return (
    <div className="mb-10 flex flex-wrap items-end justify-between gap-4 border-b border-[#241914]/15 pb-6">
      <h1 className="font-display text-4xl sm:text-5xl">{title}</h1>
      {children}
    </div>
  );
}
